/** 슬라이드 웹캠: 가능한 한 높은 해상도를 요청한다(facingMode 미사용 — 가상/모니터 캡처 장치가 기본 선택되는 경우를 줄임) */
function slideWebcamHighVideoConstraints(deviceId?: string): MediaTrackConstraints {
  const base: MediaTrackConstraints = {
    width: { ideal: 1920, min: 640 },
    height: { ideal: 1080, min: 480 },
    frameRate: { ideal: 30, max: 60 },
  };
  if (deviceId) {
    return { ...base, deviceId: { exact: deviceId } };
  }
  return base;
}

/** 화면·창 캡처로 보이는 가상 비디오 입력은 기본 후보에서 제외 */
const SCREEN_LIKE_INPUT_LABEL_RE =
  /display|화면|screen|monitor|desktop|window capture|screencapture|obs virtual|virtual desktop|ndi|슬라이드|프레젠테이션|크롬 탭|chrome tab/i;

export function filterVideoInputDevices(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
  return devices.filter((d) => d.kind === 'videoinput');
}

/** UI 목록용: (3A71:F4B5) 등 장치·USB ID 괄호 제거 */
export function simplifyVideoInputDeviceLabel(raw: string): string {
  let s = raw.trim();
  if (!s) return '카메라';
  s = s.replace(/\s*\([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)/g, '');
  s = s.replace(/\s*\(0x[0-9a-fA-F]+\)/gi, '');
  s = s.replace(/\s*\(\s*[0-9a-fA-F:.]{8,}\s*\)/gi, '');
  s = s.replace(/\s{2,}/g, ' ').trim();
  return s || '카메라';
}

/** 라벨이 있으면 화면 캡처류를 피하고, 없으면 첫 videoinput */
export function pickPreferredVideoInputId(devices: MediaDeviceInfo[]): string | undefined {
  const inputs = filterVideoInputDevices(devices);
  if (!inputs.length) return undefined;
  const withLabel = inputs.filter((d) => d.label.trim().length > 0);
  if (withLabel.length) {
    const ok = withLabel.find((d) => !SCREEN_LIKE_INPUT_LABEL_RE.test(d.label));
    return (ok ?? withLabel[0]).deviceId;
  }
  return inputs[0].deviceId;
}

export type SlideWebcamOpenOptions = {
  deviceId?: string | null;
};

export async function getSlideWebcamUserMedia(opts?: SlideWebcamOpenOptions): Promise<MediaStream> {
  const id = opts?.deviceId?.trim() || undefined;
  const video = slideWebcamHighVideoConstraints(id);
  try {
    return await navigator.mediaDevices.getUserMedia({ video, audio: false });
  } catch (e) {
    const name = e && typeof e === 'object' && 'name' in e ? String((e as { name?: string }).name) : '';
    if (name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError') {
      return navigator.mediaDevices.getUserMedia({
        video: id
          ? { deviceId: { exact: id }, width: { ideal: 1280 }, height: { ideal: 720 } }
          : { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
    }
    throw e;
  }
}

/**
 * 라벨이 비어 있으면 한 번 임시로 권한을 받은 뒤 enumerate로 선호 장치를 고른다.
 * (물리 웹캠이 없을 때 모니터 캡처 가상 장치가 기본으로 잡히는 경우 완화)
 */
export async function getSlideWebcamUserMediaSmartDefault(): Promise<MediaStream> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new Error('getUserMedia unavailable');
  }
  let devices = await navigator.mediaDevices.enumerateDevices();
  const labelsMissing = filterVideoInputDevices(devices).every((d) => !d.label.trim());
  if (labelsMissing) {
    const tmp = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    tmp.getTracks().forEach((t) => t.stop());
    devices = await navigator.mediaDevices.enumerateDevices();
  }
  const preferred = pickPreferredVideoInputId(devices);
  if (preferred) {
    try {
      return await getSlideWebcamUserMedia({ deviceId: preferred });
    } catch {
      // 장치 전환 실패 시 브라우저 기본으로 폴백
    }
  }
  return getSlideWebcamUserMedia();
}

export async function getSlideScreenShareMedia(): Promise<MediaStream> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getDisplayMedia) {
    throw new Error('이 브라우저에서는 화면 공유를 사용할 수 없습니다.');
  }
  return navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: { ideal: 30, max: 60 } },
    audio: false,
  });
}
